import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import Icon from '../components/atoms/Icon';
import { forgotPassword } from '../services/api';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState(searchParams.get('role') || 'candidate');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const userTypes = [
    { value: 'candidate', label: 'Candidate' },
    { value: 'hr', label: 'HR' },
    { value: 'course_provider', label: 'Course Provider' },
    { value: 'saas_provider', label: 'SaaS Provider' },
    { value: 'super_admin', label: 'Super Admin' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !userType) return;

    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });

    try {
      await forgotPassword({ email, userType });
      setFeedback({
        type: 'success',
        message: 'Password reset OTP sent to your email. Please check your inbox.',
      });
      
      // Redirect to reset password page with email and userType
      setTimeout(() => {
        navigate(`/reset-password?email=${encodeURIComponent(email)}&userType=${userType}`);
      }, 2000);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Failed to send reset email. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="lock_reset" className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Forgot Password?
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Enter your email address and we'll send you an OTP to reset your password.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Account Type
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {userTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setUserType(type.value)}
                    className={`p-3 text-sm font-medium rounded-lg border transition-all duration-200 ${
                      userType === type.value
                        ? 'bg-primary text-white border-primary shadow-md'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary hover:shadow-sm'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                required
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={!email || !userType || isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending OTP...
                </div>
              ) : (
                'Send Reset OTP'
              )}
            </Button>

            {/* Feedback Message */}
            {feedback.message && (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  feedback.type === 'error'
                    ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
                    : 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon 
                    name={feedback.type === 'error' ? 'error' : 'check_circle'} 
                    className="w-4 h-4" 
                  />
                  {feedback.message}
                </div>
              </div>
            )}

            {/* Back to Login */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-primary hover:underline font-medium"
              >
                ← Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
